import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { IconeFechar } from '../icones'

interface Props {
  aberta: boolean
  titulo: string
  subtitulo?: ReactNode
  aoFechar: () => void
  children: ReactNode
  /** Altura máxima, em fração da altura visível. Padrão 0,6. */
  fracaoAltura?: number
  className?: string
}

const FOCALIZAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Folha inferior — a única primitiva de bottom sheet do projeto.
 *
 * Existia uma implementação própria escondida na Central de Logs, feita só com
 * media query. Duas implementações do mesmo gesto divergem, então esta passou a
 * servir as duas: as ferramentas do módulo no celular e os registros.
 *
 * Nunca ocupa a tela inteira. O 3D é o assunto da aula, e uma folha que o
 * encobre por completo tira a referência do que se está ajustando — por isso a
 * altura é fração da tela, e não `100dvh`.
 */
export function FolhaInferior({
  aberta,
  titulo,
  subtitulo,
  aoFechar,
  children,
  fracaoAltura = 0.6,
  className = '',
}: Props) {
  const folha = useRef<HTMLDivElement>(null)
  const focoAnterior = useRef<HTMLElement | null>(null)
  // `aoFechar` costuma chegar como arrow inline e muda a cada render. Se fosse
  // dependência do efeito, cada quadro da demonstração devolveria o foco à
  // folha — e o seletor nativo de um <select> no Android fecha sozinho.
  const aoFecharAtual = useRef(aoFechar)
  aoFecharAtual.current = aoFechar

  useEffect(() => {
    if (!aberta) return

    focoAnterior.current = document.activeElement as HTMLElement | null
    folha.current?.focus()

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        aoFecharAtual.current()
        return
      }
      if (evento.key !== 'Tab' || !folha.current) return

      // Prende o Tab dentro da folha: atrás dela está o canvas e a faixa
      // inteira, e sair para lá com a folha aberta deixa o foco invisível.
      const alvos = Array.from(folha.current.querySelectorAll<HTMLElement>(FOCALIZAVEIS))
      if (alvos.length === 0) return
      const primeiro = alvos[0]!
      const ultimo = alvos.at(-1)!

      if (evento.shiftKey && document.activeElement === primeiro) {
        evento.preventDefault()
        ultimo.focus()
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault()
        primeiro.focus()
      }
    }

    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      focoAnterior.current?.focus()
    }
  }, [aberta])

  if (!aberta) return null

  // Portal para o `.app-shell`: renderizada no lugar, a folha herdava o
  // contexto de empilhamento de quem a abre — a `.barra-menu` tem `z-index: 20`,
  // e a faixa de ferramentas do celular passava por cima da folha Sobre. O alvo
  // é o shell, e não o `body`, porque é nele que mora a classe `tema-claro`.
  const destino = document.querySelector('.app-shell') ?? document.body

  return createPortal(
    <>
      {/*
        O véu escurece o canvas sem escondê-lo: tocar nele fecha.

        É uma `div`, e não um `button`, de propósito. Como botão, ele herdava a
        regra `.tema-claro button:not(.overlay-menu)`, que tem especificidade
        maior que uma classe só e o pintava de branco opaco — o canvas sumia
        atrás dele no tema claro. Escapar disso com mais um `:not()` seria
        empilhar override; o véu simplesmente não precisa ser botão: ele é
        decorativo, e quem fecha pelo teclado usa Escape ou o botão rotulado.
      */}
      <div
        className="folha-inferior__veu"
        aria-hidden="true"
        onClick={aoFechar}
      />
      <div
        ref={folha}
        className={`folha-inferior ${className}`.trim()}
        style={{ ['--fracao-altura-folha' as string]: String(fracaoAltura) }}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        tabIndex={-1}
      >
        {/* Alça: diz que a folha é arrastável e dá um alvo grande para fechar. */}
        <div className="folha-inferior__alca" aria-hidden="true" />

        <header className="folha-inferior__topo">
          <div className="folha-inferior__identidade">
            <strong>{titulo}</strong>
            {subtitulo != null && <span>{subtitulo}</span>}
          </div>
          <button
            type="button"
            className="folha-inferior__fechar"
            onClick={aoFechar}
            aria-label={`Fechar ${titulo}`}
          >
            <IconeFechar tamanho={18} />
          </button>
        </header>

        <div className="folha-inferior__corpo">{children}</div>
      </div>
    </>,
    destino,
  )
}
